<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="本所卷宗：上传到指定 Matter。可填文件 URL 或上传后自动回填。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="案件ID">
        <el-input-number v-model="query.caseId" :min="1" controls-position="right" class="!w-120px" />
      </el-form-item>
      <el-form-item label="分类">
        <el-input v-model="query.category" clearable class="!w-120px" placeholder="如 EVIDENCE" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">上传卷宗</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="120" />
      <el-table-column prop="caseTitle" label="案件" min-width="140" show-overflow-tooltip />
      <el-table-column prop="category" label="分类" width="100" />
      <el-table-column prop="title" label="标题" min-width="140" show-overflow-tooltip />
      <el-table-column prop="fileName" label="文件名" min-width="120" show-overflow-tooltip />
      <el-table-column prop="versionNo" label="版本" width="70" />
      <el-table-column label="文件" width="90">
        <template #default="{ row }">
          <el-link v-if="row.fileUrl" :href="row.fileUrl" target="_blank" type="primary">打开</el-link>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="80" fixed="right">
        <template #default="{ row }">
          <el-button link type="danger" @click="doDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="createVisible" title="上传卷宗" width="520px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%">
            <el-option
              v-for="m in matterOptions"
              :key="m.id"
              :label="`${m.firmCaseNo || m.id} · ${m.title}`"
              :value="m.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-select v-model="form.category" class="!w-100%">
            <el-option label="证据" value="EVIDENCE" />
            <el-option label="文书" value="DOCUMENT" />
            <el-option label="合同" value="CONTRACT" />
            <el-option label="其他" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" maxlength="128" />
        </el-form-item>
        <el-form-item label="文件" prop="fileUrl">
          <UploadFile v-model="form.fileUrl" :limit="1" />
          <el-input
            v-model="form.fileUrl"
            class="mt-8px"
            clearable
            placeholder="或直接粘贴文件 URL"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitCreate">保存</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import {
  FirmPracticeApi,
  type PracticeDossierVO,
  type PracticeMatterVO
} from '@/api/firm/practice'
import { UploadFile } from '@/components/UploadFile'

defineOptions({ name: 'FirmPracticeDossier' })

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeDossierVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  caseId: undefined as number | undefined,
  category: undefined as string | undefined,
  currentOnly: true
})

const matterOptions = ref<PracticeMatterVO[]>([])
const createVisible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  caseId: undefined as number | undefined,
  category: 'EVIDENCE',
  title: '',
  fileUrl: '',
  remark: ''
})
const rules: FormRules = {
  caseId: [{ required: true, message: '请选择案件', trigger: 'change' }],
  category: [{ required: true, message: '请选择分类', trigger: 'change' }],
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  fileUrl: [{ required: true, message: '请上传文件', trigger: 'change' }]
}

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPracticeApi.dossierPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const openCreate = async () => {
  form.caseId = undefined
  form.category = 'EVIDENCE'
  form.title = ''
  form.fileUrl = ''
  form.remark = ''
  createVisible.value = true
  const matters = await FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 })
  matterOptions.value = matters?.list || []
}

const submitCreate = async () => {
  await formRef.value?.validate()
  if (!form.caseId || !form.fileUrl) return
  saving.value = true
  try {
    const url = Array.isArray(form.fileUrl) ? form.fileUrl[0] : form.fileUrl
    await FirmPracticeApi.dossierCreate({
      caseId: form.caseId,
      category: form.category,
      title: form.title.trim(),
      fileUrl: String(url),
      remark: form.remark || undefined
    })
    message.success('已上传')
    createVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const doDelete = async (row: PracticeDossierVO) => {
  if (!row.id) return
  try {
    await message.confirm('确认删除该卷宗？')
  } catch {
    return
  }
  await FirmPracticeApi.dossierDelete(row.id)
  message.success('已删除')
  await getList()
}

onMounted(getList)
</script>
